import type { FunctionComponent } from 'react';

export const EulaTextEn: FunctionComponent = () => (
  <div>
    <p className='italic'>
      If you have previously entered into a customized End User License
      Agreement with IP Fabric, such agreement remains applicable until its
      term.
    </p>
    <div>
      <h1 className='text-level-3 my-lg'>
        IP FABRIC END USER LICENSE AGREEMENT
      </h1>
      <p>
        IMPORTANT: DO NOT DOWNLOAD OR USE THIS SOFTWARE UNTIL YOU (THE “
        <strong className='italic'>LICENSEE</strong>
        ”) HAVE READ AND AGREED TO THE TERMS OF THIS AGREEMENT.
      </p>
      <p>
        THIS AGREEMENT GOVERNS YOUR USE OF THE SOFTWARE AND THE MAINTENANCE
        PROVIDED WITH THE SOFTWARE.
      </p>
      <p>
        BY CLICKING ON THE “I ACCEPT” BUTTON OR DOWNLOADING, INSTALLING OR
        OTHERWISE USING THE SOFTWARE, YOU ACKNOWLEDGE AND AGREE THAT ALL SUCH
        USE IS SUBJECT TO THE TERMS AND CONDITIONS OF THIS AGREEMENT (“
        <strong className='italic'>Agreement</strong>
        ”).
      </p>
      <p>
        Any such use will constitute acceptance and result in a binding and
        legally enforceable agreement between Licensee and
      </p>
      <ol className='lower-roman'>
        <li>
          <strong>IP Fabric Inc.</strong>, if the Agreement is governed by the
          laws of the state of New York pursuant to Section 16.7, or any one of
          its subsidiaries or affiliated companies, including without
          limitation, or
        </li>
        <li>
          <strong>IP Fabric s.r.o.</strong>, if the Agreement is governed by the
          laws of the Czech Republic pursuant to Section 16.7
        </li>
        (collectively referred to as “
        <strong className='italic'>IP Fabric</strong>
        ”).
      </ol>
      <p>
        If you are accepting these terms on behalf of any corporation or other
        entity, you represent and warrant that you are authorized to legally
        bind such entity to this Agreement. If you are unauthorized to do so,
        you will enter into this Agreement yourself.
      </p>
      <ol>
        <li>
          <strong>Definitions</strong>
          <p>
            Terms with capital letters in this Agreement have the following
            meaning, unless defined elsewhere in this Agreement.
          </p>
          <p>
            <strong className='pl-7 italic'>"Affiliate"</strong> means, with
            respect to a Party, any entity which controls, is controlled by, or
            is under common control with such Party, where “control" means the
            legal, beneficial, or equitable ownership of at least a majority of
            the aggregate of all voting equity interests in such entity, but
            only for so long as such control exists.
          </p>
          <p>
            <strong className='pl-7 italic'>"Agreement"</strong> means this End
            User License Agreement.
          </p>
          <p>
            <strong className='pl-7 italic'>"Authorized User"</strong> means an
            employee or independent contractor of either Licensee or an
            Affiliate of Licensee who has been authorized by Licensee to use the
            Software in accordance with the terms and conditions of this
            Agreement.
          </p>
          <p>
            <strong className='pl-7 italic'>"Confidential Information"</strong>{' '}
            means non-public information that is disclosed by or on behalf of a
            Party under or in relation to this Agreement that is identified as
            confidential at the time of disclosure or should be reasonably
            understood to be confidential or proprietary due to the nature of
            the information and/or the circumstances surrounding its disclosure.
          </p>
          <p>
            {' '}
            Confidential Information includes, but is not limited to, all trade
            secrets, know how, proprietary information, techniques, or
            technology, and data. Confidential Information does not include
            information which, and solely to the extent it:
          </p>
          <ol className='lower-roman'>
            <li>
              is generally available to the public other than as a result of a
              disclosure by the receiving Party or any of its representatives;
            </li>
            <li>
              was known to the receiving Party prior to the date hereof on a
              non-confidential basis from a source other than disclosing Party
              or its representatives;
            </li>
            <li>
              is independently developed by the receiving Party without the
              benefit of any of the disclosing Party's Confidential Information;
            </li>
            <li>
              becomes lawfully known to the receiving Party on a
              non-confidential basis from a source (other than disclosing Party
              or its representatives) who is not prohibited from disclosing the
              information to the receiving Party by any contractual, legal,
              fiduciary or other obligation, or
            </li>
            <li>
              was disclosed by the disclosing Party to a third-party without an
              obligation of confidence. In any dispute concerning the
              applicability of these exclusions, the burden of proof will be on
              the receiving Party and such proof will be by clear and convincing
              evidence.
            </li>
          </ol>
          <p>
            <strong className='pl-7 italic'>"Delivery Date"</strong> means the
            date on which the license key(s) for such Software are initially
            made available (via download or otherwise) to Licensee, unless
            agreed upon by Parties otherwise.
          </p>
          <p>
            <strong className='pl-7 italic'>"Documentation"</strong> means the
            then-current user documentation available at the website of IP
            Fabric under the address{' '}
            <a href='https://docs.ipfabric.io' target='_blank' rel='noreferrer'>
              https://docs.ipfabric.io
            </a>
            . Documentation can be modified from time to time in sole discretion
            of IP Fabric based on the development of new versions and functions
            of the software.
          </p>
          <p>
            <strong className='pl-7 italic'>
              "Intellectual Property Claim"
            </strong>{' '}
            means a claim by a third-party against Licensee or its Affiliates
            that the Software, as delivered by IP Fabric, infringes a
            third-party copyright or trademark, or misappropriates a third-party
            trade secret.
          </p>
          <p>
            <strong className='pl-7 italic'>
              "Intellectual Property Rights"
            </strong>{' '}
            means all patents and patent rights, utility models, copyright
            rights, mask work rights, trademark rights, trade secret rights,
            service marks, product names, trade names, design rights, registered
            designs, topography rights, database rights, rights of confidence,
            know-how and all other similar intellectual property or proprietary
            rights anywhere in the world, whether or not registered or statutory
            and including, without limitation, all applications and
            registrations with respect to the foregoing.
          </p>
          <p>
            <strong className='pl-7 italic'>"Party"</strong> or{' '}
            <strong className='italic'>"Parties"</strong>
            means IP Fabric and Licensee, individually and collectively as the
            case may be.
          </p>
          <p>
            <strong className='pl-7 italic'>"Software"</strong> means a release
            of the IP Fabric (Automated Network Assurance Platform) software
            provided or made available to Licensee, as well as upgrades,
            patches, and updates and related services thereto that IP Fabric
            elects to make available at no additional charge to all of its
            customers. Unless otherwise indicated, the Software and
            Documentation are referred to collectively herein as “Software”.
          </p>
        </li>
        <li>
          <strong>License</strong>
          <ol>
            <li>
              <u>License Grant.</u> Subject to the terms and conditions of this
              Agreement, IP Fabric hereby grants to Licensee a non-exclusive,
              non-transferrable, non-sublicensable, non-commercial and personal
              license, permitting Licensee, its Affiliates and their respective
              Authorized Users to download, install and use the Software solely
              for Licensee and its Affiliates’ internal business operations and
              in accordance with the Documentation and with regard to all
              updates, patches, upgrades, bug fixes which may change the
              software (the “<strong className='italic'>License</strong>
              ”).
            </li>
            <li>
              <u>Scope of License.</u> The License level is determined based on
              the number of managed active network elements in the Customer
              environment being analyzed by the IP Fabric platform. In specific
              cases such as, switching from one vendor to another,
              decommissioning and onboarding devices within lifecycle, and
              temporary testing purposes one license can be applied to one
              device and then to another without requiring two licenses. The
              following elements are counted towards the License: all network
              devices from vendors as specified at{' '}
              <a
                href='https://matrix.ipfabric.io/'
                target='_blank'
                rel='noreferrer'
              >
                https://matrix.ipfabric.io/
              </a>{' '}
              to which access has been permitted and where analysis has been
              successfully carried out, except for wireless access points.
            </li>
            <li>
              <u>Use by Authorized Users.</u> Licensee may allow Authorized
              Users to use the Software solely on Licensee’s behalf for
              Licensee’s internal operations. Licensee shall be responsible for
              ensuring that Authorized Users comply with the terms of this
              Agreement and Licensee is liable for any breach of the same by
              such Authorized Users. To the extent permitted by applicable law,
              Licensee agrees and acknowledges that third parties using the
              Software on Licensee’s behalf bring all claims related to the
              Software to Licensee and waive all such claims directly against IP
              Fabric and its Affiliates.
            </li>
            <li>
              <u>Term.</u> Except as otherwise agreed to by both Parties in
              writing, this Agreement shall continue for an initial term of one
              (1) year following the Delivery Date (the “
              <strong>Initial Period</strong>
              ”). The Term shall mean the Initial Period and each period the
              agreement is renewed for after the Initial Period.
              <ol>
                <li>
                  Unless any of the Parties delivers to the other Party a notice
                  of non-renewal at least 30 days prior to the end of the
                  applicable Term, the Agreement shall be automatically renewed
                  for the initial length of the Term. Licensee shall send such
                  notice of non-renewal to{' '}
                  <a href='mailto:sales@ipfabric.io'>sales@ipfabric.io</a> with
                  the email title reflecting the name of client and a
                  non-renewal subject matter.
                </li>
                <li>
                  IP Fabric may also choose to send Licensee a schedule of new
                  prices for any future Term via email at least 30 days prior to
                  the end of the then current Term. In the event a new pricing
                  schedule is sent to the Licensee, there shall be no automatic
                  renewal of the Agreement until and unless Licensee signifies
                  consent to such new prices by email or other written form at
                  least 10 days prior to the end of the then current Term. Upon
                  such acceptance of the new prices by Licensee, the Agreement
                  shall be renewed with the new prices for the next Term.
                  Licensee shall send such an email to{' '}
                  <a href='mailto:sales@ipfabric.io'>sales@ipfabric.io</a>.
                </li>
              </ol>
            </li>
            <li>
              <u>License Restrictions.</u> Except as expressly permitted by IP
              Fabric, Licensee will not, nor will it permit or authorize anyone
              to:
              <ol>
                <li>
                  distribute, convey, lend, lease, share, sell, transfer,
                  market, sublicense, rent or otherwise make available any
                  component of the Software;
                </li>
                <li>
                  copy, decompile, disassemble or reverse engineer or otherwise
                  attempt to extract or derive the source code or any methods,
                  algorithms or procedures from the Software, or modify, adapt,
                  translate or create derivative works based upon the Software
                  except as expressly authorized by the mandatory provisions of
                  applicable law;
                </li>
                <li>
                  use, offer or otherwise exploit the Software as a
                  revenue-generating or commercial product or service to or for
                  any third-party, including, without limitation, hosting,
                  outsourcing, service bureau, application service provider, or
                  software as a service (SaaS) functionality;
                </li>
                <li>
                  use the Software for purposes of developing a product that is
                  competitive with any IP Fabric product or service;
                </li>
                <li>
                  remove any copyright, trademark or other proprietary notice
                  from the Software; or alter or circumvent any Software license
                  key or any other restrictions or limitations on Software
                  access or use.
                </li>
              </ol>
            </li>
            <li>
              <u>Retention of Rights.</u> The Software provided hereunder is
              licensed, not sold. To the extent not expressly licensed to
              Licensee hereunder, IP Fabric, and their respective suppliers or
              licensors where applicable, reserve and retain all right, title
              and interest in and to the Software and all Intellectual Property
              Rights embodied therein.
              <p>
                Licensee shall maintain reasonable technical and procedural
                access controls and system security to safeguard the Software
                and Documentation and shall be directly responsible for any
                violations of this Agreement by anyone that it or any of its
                Affiliates has allowed to access the Software.
              </p>
            </li>
            <li>
              <u>Feedback.</u> If Licensee chooses to provide IP Fabric with
              ideas or suggestions regarding any IP Fabric products or services,
              Licensee agrees that IP Fabric is free to use any such ideas and
              suggestions for any purpose, including, without limitation,
              developing, improving, and marketing products and services,
              without any liability or payment of any kind to Licensee.
            </li>
          </ol>
        </li>
        <li>
          <strong>Maintenance</strong>
          <ol>
            <li>
              <u>Maintenance.</u> For the whole period of the License according
              to 2.4, the Licensee under this Agreement is entitled to all
              updates, patches, upgrades, and bug fixes issued by IP Fabric,
              remote system management, 8x5 support services as set in Exhibit A
              to this Agreement (the “Maintenance”). IP Fabric is not
              responsible for providing Maintenance under this Agreement to or
              for the benefit of any Licensee:
              <ol className='lower-roman'>
                <li>
                  if the Software has been altered, revised, changed, enhanced,
                  or modified in any manner not authorized in writing in advance
                  by IP Fabric;
                </li>
                <li>
                  in connection with any bug if IP Fabric has previously
                  provided corrections for the bug;
                </li>
                <li>
                  in connection with any bugs or problems caused by errors,
                  defects, problems, alterations, revisions, changes,
                  enhancements, or modifications in the database, operating
                  system, third-party software (other than third-party software
                  bundled with the Software by IP Fabric or approved by IP
                  Fabric), hardware, or any system or networking utilized by an
                  Licensee;
                </li>
                <li>
                  if the Software or related software or systems have been
                  subjected to abuse, misuse, improper handling, accident, or
                  neglect; or
                </li>
                <li>
                  if any party other than IP Fabric (or a person or entity
                  authorized by IP Fabric) has provided any services in the
                  nature of maintenance and technical support services to the
                  Licensee with respect to the Software. IP Fabric is not
                  obligated to provide any technical support services on site at
                  the facilities of Licensee. If IP Fabric agrees, in its sole
                  discretion, to provide any technical support services on site,
                  these services will be provided only upon submission by
                  Licensee of a purchase order agreeing to pay for the services
                  on a time and materials basis.
                </li>
                <li>
                  if any party other than IP Fabric (or a person or entity
                  authorized by IP Fabric) has provided any services in the
                  nature of maintenance and technical support services to the
                  Licensee with respect to the Software. IP Fabric is not
                  obligated to provide any technical support services on site at
                  the facilities of Licensee. If IP Fabric agrees, in its sole
                  discretion, to provide any technical support services on site,
                  these services will be provided only upon submission by
                  Licensee of a purchase order agreeing to pay for the services
                  on a time and materials basis.
                </li>
                <li>
                  if any party other than IP Fabric (or a person or entity
                  authorized by IP Fabric) has provided any services in the
                  nature of maintenance and technical support services to the
                  Licensee with respect to the Software. IP Fabric is not
                  obligated to provide any technical support services on site at
                  the facilities of Licensee. If IP Fabric agrees, in its sole
                  discretion, to provide any technical support services on site,
                  these services will be provided only upon submission by
                  Licensee of a purchase order agreeing to pay for the services
                  on a time and materials basis.
                </li>
              </ol>
            </li>
            <li>
              The Software license and Maintenance provided pursuant to this
              Agreement do not include consulting, implementation, development,
              training or other services. Payment of fees under this Agreement
              shall not be contingent under any circumstances upon the
              performance of any such services.
            </li>
          </ol>
        </li>
        <li>
          <strong>Warranties and disclaimers</strong>
          <ol>
            <li>
              The Software license and Maintenance provided pursuant to this
              Agreement do not include consulting, implementation, development,
              training or other services. Payment of fees under this Agreement
              shall not be contingent under any circumstances upon the
              performance of any such services.
            </li>
            <li>
              <u>Disclaimer.</u> All licensed software, documentation and other
              products, information and materials provided by IP Fabric are
              provided "as is." IP Fabric makes no warranties with respect to
              the software or any other subject matter of this agreement and
              hereby disclaims all other warranties, whether express, implied or
              statutory, including, but not limited to warranties of title,
              non-infringement, merchantability, satisfactory quality, fitness
              for a particular purpose (even if IP Fabric has been informed of
              such purpose), and all warranties arising from course of dealing,
              usage, or trade practice. Without limiting the foregoing, IP
              Fabric makes no warranty of any kind that the licensed software or
              documentation, or any products or results of the use thereof, will
              meet licensee's or other persons' requirements, operate without
              interruption, achieve any intended result, be compatible or work
              with any software, systems, or other services, or be secure,
              accurate, complete, free of harmful code and defect or be error
              free.
            </li>
          </ol>
        </li>
        <li>
          <strong>Limitation of Liability.</strong>
          <ol>
            <li>
              <u>Limitation of liability.</u> Except for
              <ol>
                <li>death or bodily injury caused by a party’s negligence,</li>
                <li>
                  each party’s indemnification obligations under this agreement,
                </li>
                <li>
                  Licensee’s violation of IP Fabric’s intellectual property
                  rights and
                </li>
                <li>
                  the extent to which liability may not be lawfully limited
                  under the applicable law, each party's maximum, cumulative
                  liability for any claims, losses, costs (including attorney's
                  fees) and other damages arising under or related to this
                  agreement regardless of the form of action, whether in breach
                  of warranty, contract, tort (including but not limited to
                  negligence or strict liability) or otherwise, will be limited
                  to actual damages incurred, which will in no event exceed the
                  greater of the amount of fees paid by Licensee or payable by
                  the Licensee during the twelve (12) months immediately prior
                  to the time the cause of action arose and attributable to the
                  specific software, products or services giving rise to such
                  damages.
                </li>
              </ol>
            </li>
            <li>
              <u>Exclusion of damages.</u> To the maximum extent permitted under
              applicable law, IP Fabric or its affiliates will not be liable for
              <ol>
                <li>
                  any loss of any kind, whether in tort (including for
                  negligence or breach of statutory duty or strict liability),
                  contract, misrepresentation (whether innocent or negligent) or
                  otherwise,
                </li>
                <li>direct loss,</li>
                <li>accidental loss,</li>
                <li>incidental loss,</li>
                <li>
                  consequential loss, such as loss of savings, profits or
                  revenues, loss or corruption of data, goodwill, or reputation,
                  inaccuracy of any data, the cost of procurement of substitute
                  goods, services or software, or
                </li>
                <li>
                  any indirect, incidental, special, punitive, exemplary or
                  consequential damages, howsoever arising and regardless of the
                  theory of liability, even if advised of the possibility of
                  such damage or loss. IP Fabric shall use reasonable efforts to
                  protect information submitted by you in connection with the
                  services, but you agree that your submission of such
                  information is at your sole risk, and you hereby release IP
                  Fabric, to the maximum extent permitted under applicable law,
                  from any and all liability to you for any loss or liability
                  relating to such information in any way.
                </li>
              </ol>
            </li>
            <li>
              <u>Scope of Limitation.</u> The limitations, exclusions and
              disclaimers contained in this Agreement are independent of any
              agreed remedy specified in this Agreement and will apply to the
              fullest extent permitted by applicable law, even if any agreed
              remedy is found to have failed of its essential purpose. To the
              extent that IP Fabric may not, as a matter of law, disclaim any
              warranty or limit its liabilities, the scope or duration of such
              warranty and the extent of IP Fabric’s liability will be the
              minimum permitted under such law. If a waiver, right, or remedy is
              exercised pursuant to mandatory law, it shall be exercised solely
              for the purpose provided and in conformance with the procedures
              and limitations expressly provided for by such law.
            </li>
            <li>
              <u>No Third-Party Beneficiaries.</u> The warranties and other
              obligations of IP Fabric under this Agreement run only to, and for
              the sole benefit of, Licensee, notwithstanding any rights to
              access or use the Software the Licensee may grant its Affiliates
              or third-party external users. Except as otherwise mandated by
              applicable law, no other person or entity will be considered a
              third-party beneficiary of this Agreement or otherwise entitled to
              receive or enforce any rights or remedies in relation to this
              Agreement.
            </li>
          </ol>
        </li>
        <li>
          <strong>Intellectual Property Infringement Indemnification</strong>
          <ol>
            <li>
              <u>Indemnification.</u> IP Fabric shall indemnify, defend and hold
              harmless Licensee and its Affiliates against any Intellectual
              Property Claim provided that Licensee
              <ol>
                <li>
                  promptly notifies IP Fabric in writing of such Intellectual
                  Property Claim;
                </li>
                <li>
                  allows IP Fabric to have control of the defense and any
                  related settlement negotiations; and
                </li>
                <li>
                  provides IP Fabric with such information, authority (Power of
                  Attorney) and assistance necessary for the defense or
                  settlement of the Intellectual Property Claim.
                </li>
              </ol>
            </li>
            <li>
              <u>Exceptions.</u> IP Fabric will not be liable for any
              Intellectual Property Claim arising from or based upon
              <ol>
                <li>
                  any unauthorized use, reproduction or distribution of the
                  Software;
                </li>
                <li>
                  any modification or alteration of the Software without the
                  prior written approval of IP Fabric;
                </li>
                <li>
                  use of the Software in combination with any other software or
                  hardware not provided or approved by IP Fabric; or
                </li>
                <li>
                  use of the Software in a manner prohibited by any applicable
                  law.
                </li>
              </ol>
            </li>
            <li>
              <u>Remedies.</u> If the Software becomes, or, in IP Fabric’s
              opinion, is likely to become, the subject of an Intellectual
              Property Claim, IP Fabric may, at its option and expense, either:
              <ol>
                <li>
                  obtain the right for Licensee to continue using the Software
                  in accordance with this Agreement;
                </li>
                <li>
                  replace or modify the Software so that it becomes
                  non-infringing while retaining substantially similar
                  functionality; or
                </li>
                <li>
                  replace or modify the Software so that it becomes
                  non-infringing while retaining substantially similar
                  functionality; or
                </li>
                <li>
                  <u>Sole and exclusive remedy.</u> This section 6 states IP
                  Fabric's sole and entire obligation and liability, and
                  Licensee's and its Affiliates' sole and exclusive right and
                  remedy, for any Intellectual Property Claim.
                </li>
              </ol>
            </li>
          </ol>
        </li>
        <li>
          <strong>Licensee Indemnification Obligation.</strong>
          <ol>
            <li>
              <u>Indemnification.</u> Licensee is responsible for Licensee’s,
              Affiliates’ and Authorized Users’ use of the Software and Licensee
              will indemnify, hold harmless and defend IP Fabric, its Affiliates
              and their officers, directors, employees, consultants,
              subsidiaries and agents (together, the “
              <strong className='italic'>IP Fabric Entities</strong>
              ”) from and against every claim, liability, damage, loss, and
              expense, including reasonable attorneys’ fees and costs (“
              <strong className='italic'>Claim</strong>
              ”) arising out of or in any way connected with: (a) Licensee’s
              access to, use of, or alleged use of, the Software; (b) Licensee’s
              violation of any portion of this Agreement, any representation,
              warranty, or agreement referenced in this Agreement, or any
              applicable law or regulation; (c) Licensee‘s violation of any
              third-party right, including any intellectual property right or
              publicity, confidentiality, other property, or privacy right; (d)
              any dispute or issue between Licensee and any third-party; and (e)
              all claims arising from or alleging fraud, intentional misconduct,
              criminal acts, or gross negligence committed by Licensee.
            </li>
            <li>
              <u>Right to Defense.</u> To the fullest extent permitted under
              applicable law, IP Fabric Entities reserve the right, at their own
              expense, to assume the exclusive defense and control of any matter
              otherwise subject to indemnification by Licensee (without limiting
              Licensee’s indemnification obligations with respect to that
              matter), and in that case, Licensee agrees to cooperate with the
              respective IP Fabric Entity’s defense of that claim. Licensee will
              not enter into any settlement agreement in any Claim related to or
              concerning an IP Fabric Entity without prior written consent of
              the IP Fabric Entity unless such settlement provides the IP Fabric
              Entity with a complete release without imposing any additional
              costs or obligations and does not require admission of guilt or
              liability by the IP Fabric Entity.
            </li>
          </ol>
        </li>
        <li>
          <strong>Confidentiality.</strong>
          <ol>
            <li>
              <u>Obligations.</u> Each Party will hold in confidence the other
              Party's Confidential Information and will not disclose or use such
              Confidential Information except as necessary to exercise its
              express rights or perform its express obligations hereunder. Any
              Party’s disclosure of the other Party’s Confidential Information
              may be made only to those of its employees or consultants who need
              to know such information in connection herewith and who have
              agreed in writing to confidentiality terms that are no less
              restrictive than the requirements of this section. Each Party
              shall use all reasonable efforts to maintain the confidentiality
              of all of the other Party’s Confidential Information in its
              possession or control, but in no event less than the efforts that
              it ordinarily uses with respect to its own Confidential
              Information of similar nature and importance.
            </li>
            <li>
              <u>Exceptions.</u> Notwithstanding the foregoing, a Party may
              disclose the other Party's Confidential Information to the extent
              that it is required to be disclosed in accordance with an order or
              requirement of a court, administrative agency or other
              governmental body, provided that such Party, to the extent
              permitted by applicable law, provides the other Party with prompt
              notice of such order or requirement in order that it may seek a
              protective order to protect the confidentiality of such
              information, take action to assure confidential handling of the
              Confidential Information, or take such other action as it deems
              appropriate to protect the Confidential Information. Each Party's
              confidentiality obligations hereunder will continue for a period
              of five (5) years following any termination of this Agreement,
              provided, however, that each Party's obligations will survive and
              continue in effect thereafter with respect to, and for so long as,
              any Confidential Information continues to be a trade secret under
              applicable law. The Parties acknowledge and agree that the
              Software and all pricing information shall be treated as the
              Confidential Information of IP Fabric.
            </li>
          </ol>
        </li>
        <li>
          <strong>Termination</strong>
          <ol>
            <li>
              <u>Termination for Breach.</u> Either Party may terminate this
              Agreement if the other Party fails to cure a material breach
              within thirty (30) days after written notice (email sufficient) of
              such breach is received by the breaching Party, provided that IP
              Fabric may terminate this Agreement immediately upon any breach of
              Sections 2.5 and 2.6.
            </li>
            <li>
              <u>Effect of Termination.</u> Upon expiration or termination of
              this Agreement or any Software license, all rights granted to
              Licensee under this Agreement will also terminate and Licensee
              shall: (i) immediately cease using the applicable Software and
              Documentation; and (ii) certify to IP Fabric within thirty (30)
              days after such expiration or termination that Licensee has
              destroyed or has returned to IP Fabric all copies of the
              applicable Software, any associated license keys, the
              Documentation and all other IP Fabric Confidential Information in
              its possession. Termination of this Agreement shall not prevent
              either Party from pursuing all available legal remedies, nor shall
              such termination relieve Licensee's obligation to pay all fees
              that are owed as of the effective date of termination. All
              provisions of this Agreement relating to IP Fabric’s ownership of
              the Software, limitations of liability, disclaimers of warranties,
              confidentiality (for the time periods specified in this
              Agreement), waiver, audit, governing law and jurisdiction, in
              addition to any other provisions of this Agreement that would
              normally survive termination, shall survive termination of this
              Agreement for any reason. The Licensee has no right to refund any
              and all payments already done under this Agreement.
            </li>
          </ol>
        </li>
        <li>
          <strong>Export Control.</strong> The Software may be subject to US
          export control laws, including the US Export Control Reform Act and
          its associated regulations. Licensee agrees that Licensee will not
          ship, transfer or export the Software into any country, or use the
          Software in any manner, prohibited by the United States Bureau of
          Industry and Security or economic sanctions regulations administered
          by the U.S. Department of Treasury’s Office of Foreign Assets Control
          (“
          <strong className='italic'>OFAC</strong>
          ”), or any applicable export laws, restrictions or regulations. These
          laws include restrictions on destinations, end users and end use. By
          accepting this Agreement, Licensee confirms that Licensee is not a
          resident or citizen of any country currently embargoed by the U.S. or
          the EU and that Licensee is not otherwise prohibited from receiving
          the Software. Licensee shall comply with all applicable laws and
          complete all required undertakings (including obtaining any necessary
          export license or other governmental approval) prior to exporting,
          re-exporting, releasing, or otherwise making the Software available
          outside the US.
        </li>
        <li>
          <strong>US Government Rights.</strong> Each of the Documentation and
          the software components that constitute the Software is a “commercial
          item” as that term is defined at 48 C.F.R. 2.101, consisting of
          “commercial computer software” and “commercial computer software
          documentation” as such terms are used in 48 C.F.R. 12.212.
          Accordingly, if Licensee is an agency of the US Government or any
          contractor therefor, Licensee only receives those rights with respect
          to the Software and Documentation as are granted to all other end
          users under license, in accordance with (a) 48 C.F.R. §227.7201
          through 48 C.F.R. §227.7204, with respect to the Department of Defense
          and their contractors, or (b) 48 C.F.R. §12.212, with respect to all
          other US Government licensees and their contractors.
        </li>
        <li>
          <strong>Force Majeure.</strong> Neither Party shall be liable in the
          event that its performance of this Agreement is prevented, or rendered
          so difficult or expensive as to be commercially impracticable, by
          reason of an act of God, disease, epidemic, pandemic, quarantine,
          labor dispute, unavailability of transportation, goods or services,
          governmental restrictions or actions, passage of law or any action
          taken by a governmental or public authority, including imposing an
          export or import restriction, quota, or other restriction or
          prohibition or any complete or partial government shutdown, war
          (declared or undeclared) or other hostilities, or by any other event,
          condition or cause which is not foreseeable on the Effective Date and
          is beyond the reasonable control of the Party. It is expressly agreed
          that any failure of the United States Government to issue a required
          license for the export of any Software ordered by Licensee shall
          constitute an event of force majeure. In the event of non-performance
          or delay in performance attributable to any such causes, the period
          allowed for performance of the applicable obligation under this
          Agreement will be extended for a period equal to the period of the
          delay. However, the Party so delayed shall use its best efforts,
          without obligation to expend substantial amounts not otherwise
          required under this Agreement, to remove or overcome the cause of
          delay. In the event that the performance of a Party is delayed for
          more than six (6) months, the other Party shall have the right, which
          shall be exercisable for so long as the cause of such delay shall
          continue to exist, to terminate this Agreement without liability for
          such termination.
        </li>
        <li>
          <strong>Rules of Conduct.</strong> Licensee agrees to comply with all
          local, state, federal, and national laws, statutes, ordinances, and
          regulations that apply to Licensee’s use of the Software. Licensee may
          not (i) create, use, share and/or publish by any means in relation to
          the Software any material (text, words, images, sounds, videos, etc.)
          which would breach of a duty of confidentiality, infringe any
          intellectual property right or an individual’s right to privacy or
          which would incite the committing of an unlawful act (in particular,
          piracy, cracking or circulation of counterfeit software); (b) modify,
          distort, block, abnormally burden, disrupt, slow down and/or hinder
          the normal functioning of all or part of the Software, or its
          accessibility to other users, or the functioning of the partner
          networks of the Software, or attempt to do any of the above; (c)
          transmit or propagate any virus, trojan horse, worm, bomb, corrupted
          file and/or similar destructive device or corrupted data in relation
          to the Software, and/or organize, participate in or be involved in any
          way in an attack on IP Fabric’s servers and/or the Software; (d)
          create, supply or use alternative methods of using the Software; (e)
          make inappropriate use of the help service or send untruthful reports
          to IP Fabric; (f) falsely claim to be an employee or representative of
          IP Fabric; or (g) falsely claim an endorsement in connection with the
          Software or with IP Fabric. These rules of conduct are not meant to be
          exhaustive and can be modified at any time by IP Fabric. In all cases,
          Licensee may only use the Software according to anticipated use of the
          Software.
        </li>
        <li>
          <strong>Audit and Recordkeeping.</strong> While this Agreement is in
          effect and for one (1) year after the effective date of its
          termination, upon request by IP Fabric but not more than once per
          calendar year, Licensee shall conduct a self-audit of its use of the
          Software and, within ten (10) business days after receipt of such
          request, submit a written statement to IP Fabric verifying that it is
          in compliance with the terms and conditions of this Agreement. Such
          self-audit shall be in compliant with industry standards. IP Fabric
          shall have the right, on its own or through its designated agent or
          third-party accounting firm, to conduct an on-premises audit of
          Licensee's use and deployment of the Software for compliance with this
          Agreement. IP Fabric’s written request for audit will be submitted to
          Licensee at least fifteen (15) days prior to the specified audit date,
          and such audit shall be conducted during regular business hours and
          with the goal of minimizing the disruption to Licensee's business. If
          such audit discloses that Licensee is not in material compliance with
          the terms of this Agreement, then Licensee shall be responsible for
          the reasonable costs of the audit, in addition to any further fees or
          damages to which IP Fabric would be entitled under this Agreement and
          applicable law.
        </li>
        <li>
          <strong>Software Update.</strong> From time to time, IP Fabric may
          automatically update or otherwise modify the Software, including, but
          not limited to, for purposes of enhancement of security functions,
          error correction and improvement of functions. Such updates or
          modifications may delete or change the nature of features or other
          aspects of the Software, including, but not limited to, functions you
          may rely upon. You acknowledge and agree that such activities may
          occur at IP Fabric's sole discretion and that IP Fabric may condition
          continued use of the Software upon your complete installation or
          acceptance of such update or modifications. Any updates or
          modifications shall be deemed to be, and shall constitute part of, the
          Software for purposes of this Agreement. By acceptance of this
          Agreement, you consent to such update or modification.
        </li>
        <li>
          <strong>General Provisions</strong>
          <ol>
            <li>
              <u>Third Party Materials.</u> The Software has requirements for
              third party software such as internet browser, operating systems
              and other standard utilities in order to function and the Software
              itself may include certain open source or other third-party
              software, data, or other materials (the “
              <strong className='italic'>Third-Party Materials</strong>
              ”) that are separately licensed by their respective owners.
              License terms and other information relating to such Third-Party
              Materials, including any availability of source code, may be found
              within the Documentation. IP Fabric makes no representation,
              warranty, or other commitment regarding the Third-Party Materials,
              and hereby disclaims any and all liability relating to Licensee’s
              use thereof.
            </li>
            <li>
              <u>Disclosures.</u> Licensee hereby grants IP Fabric the right to
              take steps IP Fabric believes are reasonably necessary or
              appropriate to enforce and/or verify compliance with any part of
              this Agreement. Licensee agrees that IP Fabric has the right,
              without liability to you, to disclose any data and/or information
              to law enforcement authorities, government officials, and/or a
              third-party, as IP Fabric believes is reasonably necessary or
              appropriate to enforce and/or verify compliance with any part of
              this Agreement (including but not limited to IP Fabric's right to
              cooperate with any legal process relating to your use of the
              Software, and/or a third-party claim that your use of the Software
              is unlawful and/or infringes such third-party's rights).
            </li>
            <li>
              <u>Waiver and Severability.</u> Any waiver or failure to enforce
              any provision of this Agreement on one occasion will not be deemed
              a waiver of any other provision or of such provision on any other
              occasion. If any provision of this Agreement is adjudicated to be
              unenforceable, such provision will be changed and interpreted to
              accomplish the objectives of such provision to the greatest extent
              possible under applicable law and the remaining provisions will
              continue in full force and effect.
            </li>
            <li>
              <u>Assignment.</u> Licensee will not assign or transfer this
              Agreement or its rights and obligations hereunder to any
              third-party without the prior written consent of IP Fabric. For
              purposes of this Section, any change of control of Licensee,
              whether by merger, sale of equity interests or otherwise, will
              constitute an assignment requiring the prior written consent of IP
              Fabric.
            </li>
            <li>
              <u>Information Practices.</u> In order to verify the identity and
              contact information of Licensee and/or to support Licensee and
              improve the customer experience with the Software, IP Fabric may
              collect and use certain information relating to the use of the
              Software. This information may include size and number of
              databases and document objects, session information (e.g., number,
              duration, error messages, types/number of users and applications
              used) and browser and server configurations.
            </li>
            <li>
              <u>Amendment.</u> IP Fabric reserves the right, in its sole and
              absolute discretion, to revise, update, change or modify certain
              terms of this Agreement for security, legal, best practice or
              regulatory reasons about which IP Fabric shall inform Licensee.
              Licensee is encouraged to review IP Fabric’s website, and any
              notifications, messages and communications from us to ensure
              Licensee is abreast of any changes as they are made. Ongoing use
              of the Software implies consent to and acceptance of any changes.
            </li>
            <li>
              <u>Governing Law.</u> Except to the extent expressly provided in
              the following paragraph, this Agreement and all matters arising
              out of it shall be governed by, and construed in accordance with,
              the laws of the state of Czech Republic without regard to any
              conflict of laws principles.
              <p>
                If (a) you are a U.S. or Canadian citizen or entity; (b) you do
                reside in the U.S. or in Canada; (c) you are accessing the
                Service from the U.S. or from Canada; the governing law of this
                Agreement is the law of New York but excluding any conflict of
                law rules.
              </p>
              <p>
                This Agreement shall not be governed by the United Nations
                Conventions on Contracts for the International Sale of Goods,
                the application of which is hereby expressly excluded from any
                interpretation of this Agreement.
              </p>
            </li>
            <li>
              <u>Dispute Resolution.</u>
              <ol>
                <li>
                  Except to the extent expressly provided in the following
                  sub-section, all disputes arising from or relating to this
                  Agreement shall be finally decided with the Arbitration Court
                  attached to the Czech Chamber of Commerce and the Agricultural
                  Chamber of the Czech Republic according to its Rules by three
                  arbitrators in accordance with the Rules of that Arbitration
                  Court. IP Fabric and the Licensee agree that the arbitration
                  award may be reviewed by other arbitrators at the request of
                  either party. The request for review shall be submitted by the
                  requesting party to the other party and also to the arbitral
                  tribunal no later than fifteen (15) days from the date on
                  which the arbitration award was received. The Rules of that
                  Arbitration Court will apply. The reviewers may change or
                  confirm the contested judgment by an arbitration award. The
                  arbitral award made in the proceedings under this paragraph is
                  final and binding. If one of the parties fails to submit a
                  request for review pursuant to the preceding paragraph in due
                  time, the arbitral award given in the original arbitration
                  shall be final and binding.
                </li>
                <li>
                  If this Agreement is governed by the laws of the state of New
                  York, all disputes arising from or relating to this Agreement
                  shall be submitted to arbitration solely. Demand shall be made
                  to the American Arbitration Association and shall be conducted
                  in New York by one arbitrator(s), unless the Parties mutually
                  agree otherwise. Arbitration shall be in accordance with the
                  commercial rules of the American Arbitration Association. The
                  Award of the Arbitrator shall be final, and judgment may be
                  entered upon it in any court having jurisdiction thereof, and
                  the prevailing party shall be entitled to costs and reasonable
                  attorney's fees arising out of such arbitration.
                </li>
              </ol>
            </li>
            <li>
              <u>Entire Agreement.</u> This Agreement constitutes the entire
              agreement between Licensee and IP Fabric and governs Licensee’s
              use of the Software, superseding any prior agreements with respect
              to the same subject matter between Licensee and IP Fabric. In the
              event that Licensee installs the IP Fabric Product as defined in
              the terms and conditions of IP Fabric, Licensee will be bound by
              the terms of such terms and conditions. Licensee also may be
              subject to additional terms and conditions that may apply when
              Licensee uses affiliate services, third-party content, third-party
              software, or additional services.
            </li>
            <li>
              <u>Notifications and Contact.</u> IP Fabric may notify you with
              respect to the Software by posting on the website, sending an
              email message to your email address or a letter via postal mail to
              your mailing address. Notices shall become effective immediately.
              IP Fabric may also contact you by email or push notification to
              send you additional information about the Software. For any
              question concerning this Agreement, you may contact IP Fabric via
              the following:
              <p>
                <strong>For US/Canadian entity or citizen:</strong>
              </p>
              <p>IP Fabric, Inc.</p>
              <p>115 Broadway, 5th Floor</p>
              <p>New York</p>
              <p>NY 10006 - 1604 United States,</p>
              <p>
                <strong>Email</strong>{' '}
                <a href='mailto:info@ipfabric.io'>info@ipfabric.io</a>
              </p>
              <br />
              <p>
                <strong>For any other entity or citizen:</strong>
              </p>
              <p>IP Fabric s.r.o.</p>
              <p>Kateřinská 466/40, Nové Město, 120 00 Praha 2</p>
              <p>
                Email: <a href='mailto:sales@ipfabric.io'>sales@ipfabric.io</a>
              </p>
            </li>
          </ol>
        </li>
      </ol>
    </div>
    <div>
      <h1>
        <strong>
          <u>EXHIBIT A – SUPPORT AND SERVICE LEVEL AGREEMENT</u>
        </strong>
      </h1>
      <h2>
        <strong>Maintenance parameters and definitions</strong>
      </h2>
      <p>
        The following details Maintenance parameters and definitions which are
        responsibility of IP Fabric in the ongoing support of this Agreement.
      </p>
      <ol>
        <li>
          <strong>Support service definition</strong>
          <p>
            Support service means IP Fabric's response to the Licensee's
            inquiries related to the installation, configuration or standard use
            of the Software, which may be resolved by: (i) directing the
            Licensee to relevant documentation or other sources of information,
            (ii) providing advice for installation, configuration or use of the
            Software. Guaranteed service parameters apply to the use of the
            Software in compliance with Software documentation located at{' '}
            <a href='https://docs.ipfabric.io' target='_blank' rel='noreferrer'>
              https://docs.ipfabric.io
            </a>{' '}
            for supported platforms and technologies as defined in compatibility
            matrix located at{' '}
            <a
              href='https://docs.ipfabric.io/matrix'
              target='_blank'
              rel='noreferrer'
            >
              https://docs.ipfabric.io/matrix
            </a>
            .
          </p>
        </li>
        <li>
          <strong>Support service classification</strong>
          <p>
            While each support request is unique, classification system is used
            to define priority of the support request as follows
          </p>
          <ol>
            <li>
              Priority 1 - Critical. Critical issue which prevents overall work
              with the Software. Software is not operational, or performance is
              severely degraded.
            </li>
            <li>
              Priority 2 - High. The Software is impaired, but most operations
              remain functional. For example, bugs affecting performance of the
              system and accuracy of discovery.
            </li>
            <li>
              Priority 3 - Normal. Other non-critical issue that has little or
              no impact on Software operation. Bugs are affecting only part of
              the system. For example, incorrect calculation of analytical
              information for a specific use-case, information needed about
              installation, capabilities or configuration is required.
            </li>
          </ol>
        </li>
        <li>
          <strong>Support service availability</strong>
          <p>
            Unless otherwise agreed by the Parties, IP Fabric will provide
            support during working days, Monday to Friday except Bank holidays,
            between 9:00 and 17:00 CET. IP Fabric undertakes to make reasonable
            efforts to respond to the Licensee's inquiries as soon as possible.
          </p>
          <ol>
            <li>
              <strong>Contact details</strong>
              <p>
                IP Fabric service desk can be contacted via email or by using
                web interface. The contact details for the service desk are as
                follows:
              </p>
              <ol>
                <li>
                  Email:{' '}
                  <a href='mailto:support@ipfabric.io'>support@ipfabric.io</a>
                </li>
                <li>
                  Web:{' '}
                  <a
                    href='support.ipfabric.io'
                    target='_blank'
                    rel='noreferrer'
                  >
                    support@ipfabric.io
                  </a>
                </li>
              </ol>
            </li>
            <li>
              <strong>Guaranteed first response time</strong>
              <p>
                The guaranteed first response time is calculated from the
                reporting of the issue by an authorized person of the Licensee
                to the first response from IP Fabric service desk personnel. The
                guaranteed first response time depends on classification of the
                issue and is set as follows:
              </p>
              <ol>
                <li>Priority 1: 1 hour during business hours.</li>
                <li>Priority 2: 4 hours during business hours.</li>
                <li>Priority 3: Next working day.</li>
              </ol>
            </li>
            <li>
              <strong>Guaranteed reaction time</strong>
              <p>
                The guaranteed reaction time is a maximal time calculated from
                the response of the Licensee to a query from the Support team to
                another response from the IP Fabric Support team. The guaranteed
                reaction time depends on classification of the issue and is set
                as follows:
              </p>
              <ol>
                <li>Priority 1: 4 hours during business hours.</li>
                <li>Priority 2: Next working day.</li>
                <li>
                  Priority 3: Next two working days.
                  <table className='table-bordered table'>
                    <caption className='italic'>
                      Table 1: Support Service issue classification and
                      guaranteed times
                    </caption>
                    <tbody>
                      <tr>
                        <th>Priority</th>
                        <td>Priority 1</td>
                        <td>Priority 2</td>
                        <td>Priority 3</td>
                      </tr>
                      <tr>
                        <th>Issue Type</th>
                        <td>Critical Issue</td>
                        <td>High priority issue</td>
                        <td>Other non-critical issue</td>
                      </tr>
                      <tr>
                        <th>First response time</th>
                        <td>1 hour during business hours</td>
                        <td>4 hours during business hours</td>
                        <td>Next working day</td>
                      </tr>
                      <tr>
                        <th>Reaction time</th>
                        <td>4 hours during business hours</td>
                        <td>Next working day</td>
                        <td>Next two working days</td>
                      </tr>
                    </tbody>
                  </table>
                </li>
              </ol>
            </li>
          </ol>
        </li>
      </ol>
    </div>
  </div>
);
